import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../components/context/AuthProvider";
type Category = "account" | "world" | "chronicle" | "hero" | "session";
interface Term {
  num: string;
  name: string;
  description: React.ReactNode;
  tags: string[];
  cat: Category;
  keywords: string;
}
const TERMS: Term[] = [
  {
    num: "01",
    name: "Hearth",
    description:
      "Your personal home base after sign-in. A live summary of Entries, Chronicles, and Heroes — the warm place where every adventure begins.",
    tags: ["Navigation", "Page title"],
    cat: "account",
    keywords: "hearth home navigation summary dashboard",
  },
  {
    num: "02",
    name: "Weaver Profile",
    description:
      "Your account page — display name, avatar, and platform preferences. A Weaver crafts stories and worlds on RoleWeave.",
    tags: ["Nav dropdown", "Page heading"],
    cat: "account",
    keywords: "weaver profile account settings avatar user",
  },
  {
    num: "03",
    name: "Compendium",
    description:
      "Your private repository of world-building material. Stores Entries in a searchable hierarchy. Private by default; Entries can be shared into Chronicles.",
    tags: ["Navigation", "Page title", "Breadcrumbs"],
    cat: "world",
    keywords: "compendium library repository hierarchy asset",
  },
  {
    num: "04",
    name: "Entry",
    description:
      "A single piece of world-building content — NPC, location, faction, item, or lore. Contains Folios and links to other Entries via Weave Links.",
    tags: ["Page headings", "Buttons"],
    cat: "world",
    keywords: "entry npc location faction item lore asset",
  },
  {
    num: "05",
    name: "Folio",
    description:
      'A named content block inside an Entry — e.g. "Appearance", "History", "Secrets". Each Folio has its own rich-text body and a Revelation visibility setting.',
    tags: ["Edit page headings"],
    cat: "world",
    keywords: "folio block appearance history secrets section chapter",
  },
  {
    num: "06",
    name: "Weave Link",
    description:
      "Inline [[Entry Name]] in Folio text rendering as a clickable link to another Entry. Builds a connected lore web without duplication.",
    tags: ["Warning dialogs"],
    cat: "world",
    keywords: "weave link inline reference [[ lore web",
  },
  {
    num: "07",
    name: "Chronicle",
    description:
      "A collaborative game or story with a GM owner, Companions, Chronicle Entries from the Compendium, and a full Session history.",
    tags: ["Navigation", "Page titles", "Buttons"],
    cat: "chronicle",
    keywords: "chronicle campaign game story gm companions sessions",
  },
  {
    num: "08",
    name: "Companion",
    description:
      "Any user in a Chronicle — GM or player. Each Companion's role controls visibility and editing rights within the Chronicle.",
    tags: ["Session UI", "Invite flow"],
    cat: "chronicle",
    keywords: "companion player gm role visibility editing participant",
  },
  {
    num: "09",
    name: "Summons",
    description:
      "An invitation link to join a Chronicle as a Companion. The acceptance page shows Chronicle details before the user commits.",
    tags: ["Invite page heading"],
    cat: "chronicle",
    keywords: "summons invite join link acceptance invitation",
  },
  {
    num: "10",
    name: "Chronicle Entry",
    description:
      "An Entry added to a Chronicle, available during sessions. Sourced from a Companion's Compendium; visibility governed by Revelations.",
    tags: ["Edit headings", "Panel sub-headings"],
    cat: "chronicle",
    keywords: "chronicle entry shared compendium revelation campaign asset",
  },
  {
    num: "11",
    name: "Revelation",
    description:
      "A per-Folio visibility rule. Lets GMs keep secrets until the perfect dramatic moment — then reveal them to specific Companions mid-session.",
    tags: ["Access list UI"],
    cat: "chronicle",
    keywords: "revelation visibility secret folio companions gm hidden access control",
  },
  {
    num: "12",
    name: "Hero",
    description:
      "A character sheet — statistics, traits, backstory. Linked to a Companion slot in a Chronicle; viewable in sessions via the Hero Sheet panel.",
    tags: ["Navigation", "Page titles", "Buttons"],
    cat: "hero",
    keywords: "hero character sheet stats backstory traits",
  },
  {
    num: "13",
    name: "Fate Formula",
    description:
      'A saved dice-roll config — e.g. "2d6 + 3 (Attack)". Attached to a Hero and shown as one-click shortcuts in the Dice Roller panel during sessions.',
    tags: ["Manager heading", "Form heading"],
    cat: "hero",
    keywords: "fate formula dice roll shortcut attack check save template",
  },
  {
    num: "14",
    name: "Session",
    description:
      "A single game meeting within a Chronicle. Contains a Party Log, Field Notes, and Annals. All real-time collaboration lives inside an active Session.",
    tags: ["Core concept"],
    cat: "session",
    keywords: "session meeting game real-time chronicle",
  },
  {
    num: "15",
    name: "Party Log",
    description:
      "Shared notes visible to all Companions — discoveries, decisions, and story beats written collaboratively during a Session.",
    tags: ["Notes panel tab"],
    cat: "session",
    keywords: "party log shared notes companions communal session notes",
  },
  {
    num: "16",
    name: "Field Notes",
    description:
      "Private notes visible only to you — for personal observations, secret plans, and reminders you don't want to share with the party.",
    tags: ["Notes panel tab"],
    cat: "session",
    keywords: "field notes personal private secret observations plans",
  },
  {
    num: "17",
    name: "The Hearth",
    description:
      "The real-time chat channel during a Session — in-character or out-of-character. A warm gathering place for the whole party.",
    tags: ["Chat panel title", "HUD tooltip"],
    cat: "session",
    keywords: "the hearth chat conversation in-character party session chat",
  },
  {
    num: "18",
    name: "Annals",
    description:
      "The automated, chronological record of a Session: dice rolls, chat, notes, and GM events. Reviewable as a floating panel during and after sessions.",
    tags: ["Panel title", "Toggle button"],
    cat: "session",
    keywords: "annals record log dice rolls events chronological session log",
  },
  {
    num: "19",
    name: "Turn Order",
    description:
      "Manages combat and structured encounters. Add Companions and NPCs, set values, step through turns. Works for any structured scene.",
    tags: ["Panel title", "Section heading"],
    cat: "session",
    keywords: "turn order combat encounter npcs structured turns initiative tracker",
  },
  {
    num: "20",
    name: "GM Sanctum",
    description:
      "A floating panel exclusively for the GM — Chronicle Entries, encounter controls, and tools invisible to players.",
    tags: ["Panel title"],
    cat: "session",
    keywords: "gm sanctum only encounter control private entries dm tools",
  },
];
const CHAPTERS: { cat: Category; label: string; count: number }[] = [
  { cat: "account", label: "Account", count: 2 },
  { cat: "world", label: "World-building", count: 4 },
  { cat: "chronicle", label: "Chronicles", count: 5 },
  { cat: "hero", label: "Heroes", count: 2 },
  { cat: "session", label: "Sessions", count: 7 },
];
const Lexicon: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState<Category | "">("");
  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return TERMS.filter((t) => {
      const catOk = !activeCat || t.cat === activeCat;
      const qOk = !q || t.keywords.includes(q) || t.name.toLowerCase().includes(q);
      return catOk && qOk;
    });
  }, [query, activeCat]);
  const visibleChapters = useMemo(
    () =>
      CHAPTERS.filter((ch) => {
        const catOk = !activeCat || ch.cat === activeCat;
        const hasTerms = filtered.some((t) => t.cat === ch.cat);
        return catOk && hasTerms;
      }),
    [activeCat, filtered]
  );
  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Nav */}
      <header className="fixed left-0 right-0 top-0 z-50 flex items-center justify-between border-b border-zinc-900 bg-zinc-950/80 px-8 py-4 backdrop-blur-sm">
        <div className="flex select-none items-center gap-3">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo192.png" alt="RoleWeave" className="h-8 w-8 object-contain" />
            <span className="text-xl font-light tracking-widest">
              Role<span className="font-normal text-amber-400">Weave</span>
            </span>
          </Link>
          <span className="mx-1 text-zinc-800">·</span>
          <span className="text-xs uppercase tracking-widest text-zinc-500">Lexicon</span>
        </div>
        <nav className="flex items-center gap-6">
          <Link to="/lexicon" className="text-sm tracking-wide text-amber-400">
            Lexicon
          </Link>
          <Link
            to="/guide"
            className="text-sm tracking-wide text-zinc-400 transition-colors duration-200 hover:text-white"
          >
            Guide
          </Link>
          {isAuthenticated ? (
            <Link
              to="/protected/home"
              className="border border-amber-400/50 px-5 py-2 text-sm tracking-wide text-amber-400 transition-all duration-200 hover:bg-amber-400 hover:text-zinc-950"
            >
              Hearth →
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                className="text-sm tracking-wide text-zinc-400 transition-colors duration-200 hover:text-white"
              >
                Sign in
              </Link>
              <Link
                to="/register"
                className="border border-amber-400/50 px-5 py-2 text-sm tracking-wide text-amber-400 transition-all duration-200 hover:bg-amber-400 hover:text-zinc-950"
              >
                Get started
              </Link>
            </>
          )}
        </nav>
      </header>
      {/* Hero */}
      <section className="border-b border-zinc-900 px-8 pb-16 pt-40">
        <div className="mx-auto max-w-5xl">
          <p className="mb-6 text-xs uppercase tracking-[0.45em] text-amber-400/80">
            Where stories are stitched, not rolled
          </p>
          <h1 className="mb-10 text-7xl font-thin leading-none tracking-tight md:text-9xl">
            The <span className="text-amber-400">Lexicon</span>
          </h1>
          <div className="flex flex-col justify-between gap-8 md:flex-row md:items-end">
            <p className="max-w-md text-base font-light leading-relaxed text-zinc-400">
              20 concepts. Each one precisely named and clearly defined. The language of RoleWeave.
            </p>
            <div className="flex w-full flex-shrink-0 items-center gap-0 border border-zinc-800 md:max-w-xs">
              <span className="px-4 text-zinc-500">&#8981;</span>
              <input
                type="text"
                placeholder="Search..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="flex-1 bg-transparent py-3 text-sm text-white placeholder-zinc-600 outline-none"
              />
            </div>
          </div>
        </div>
      </section>
      {/* Sticky category tabs */}
      <div className="sticky top-[57px] z-40 border-b border-zinc-900 bg-zinc-950/90 backdrop-blur-sm">
        <div className="mx-auto flex max-w-5xl flex-wrap gap-2 px-8 py-4">
          <button
            onClick={() => setActiveCat("")}
            className={`cpill${activeCat === "" ? " cpill-active" : ""}`}
          >
            All concepts
          </button>
          {CHAPTERS.map((ch) => (
            <button
              key={ch.cat}
              onClick={() => setActiveCat(activeCat === ch.cat ? "" : ch.cat)}
              className={`cpill${activeCat === ch.cat ? " cpill-active" : ""}`}
            >
              {ch.label}
            </button>
          ))}
        </div>
      </div>
      {/* Entries */}
      <main className="mx-auto max-w-5xl px-8">
        {filtered.length === 0 && (
          <p className="py-20 text-center text-sm tracking-wide text-zinc-600">
            No concepts match your search.
          </p>
        )}
        {visibleChapters.map((ch) => {
          const chTerms = filtered.filter((t) => t.cat === ch.cat);
          return (
            <div key={ch.cat}>
              <div className="mt-4 flex items-center gap-6 border-b border-zinc-900 py-8">
                <p className="text-xs uppercase tracking-[0.45em] text-amber-400/80">{ch.label}</p>
                <div className="h-px flex-1 bg-zinc-900" />
                <span className="font-mono text-xs text-zinc-700">{ch.count} terms</span>
              </div>
              {chTerms.map((term) => (
                <div
                  key={term.num}
                  className="group flex items-start gap-8 border-b border-zinc-900 py-10 transition-colors duration-150 last:border-0 hover:bg-zinc-950/80"
                >
                  <span className="w-20 flex-shrink-0 text-5xl font-thin leading-none text-zinc-800 transition-colors duration-200 group-hover:text-amber-400 md:w-28 md:text-7xl">
                    {term.num}
                  </span>
                  <div className="flex-1 pt-1">
                    <h3 className="mb-4 text-xl font-light tracking-wide text-white md:text-2xl">
                      {term.name}
                    </h3>
                    <p className="mb-5 text-sm leading-relaxed text-zinc-400">{term.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {term.tags.map((tag) => (
                        <span key={tag} className="lex-tag">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </main>
      {/* CTA */}
      <section className="mt-8 border-t border-zinc-900 px-6 py-32 text-center">
        <div className="mx-auto max-w-2xl">
          <p className="mb-10 text-xs uppercase tracking-[0.45em] text-amber-400/80">Begin</p>
          <h2 className="mb-6 text-4xl font-thin leading-tight tracking-wide text-white md:text-5xl">
            Your story is waiting
            <br />
            to be woven.
          </h2>
          {isAuthenticated ? (
            <>
              <p className="mb-12 text-sm text-zinc-400">Welcome back. Your world awaits.</p>
              <Link
                to="/protected/home"
                className="inline-block bg-amber-400 px-10 py-4 text-xs font-semibold uppercase tracking-widest text-zinc-950 transition-colors duration-200 hover:bg-amber-300"
              >
                Continue weaving
              </Link>
            </>
          ) : (
            <>
              <p className="mb-12 text-sm text-zinc-400">Free to start. No credit card required.</p>
              <Link
                to="/register"
                className="inline-block bg-amber-400 px-10 py-4 text-xs font-semibold uppercase tracking-widest text-zinc-950 transition-colors duration-200 hover:bg-amber-300"
              >
                Start weaving
              </Link>
            </>
          )}
        </div>
      </section>
      {/* Footer */}
      <footer className="flex flex-col items-center justify-between gap-4 border-t border-zinc-900 px-8 py-10 sm:flex-row">
        <div className="flex select-none items-center gap-2">
          <img src="/logo192.png" alt="RoleWeave" className="h-5 w-5 object-contain opacity-70" />
          <span className="text-xs tracking-widest text-zinc-500">
            Role<span className="text-amber-400/80">Weave</span>
          </span>
        </div>
        <p className="text-xs text-zinc-500">© 2026 RoleWeave. All rights reserved.</p>
        <div className="flex gap-6">
          <Link
            to="/lexicon"
            className="text-xs text-zinc-500 transition-colors duration-200 hover:text-zinc-300"
          >
            Lexicon
          </Link>
          <Link
            to="/guide"
            className="text-xs text-zinc-500 transition-colors duration-200 hover:text-zinc-300"
          >
            Guide
          </Link>
          {isAuthenticated ? (
            <Link
              to="/protected/home"
              className="text-xs text-zinc-500 transition-colors duration-200 hover:text-zinc-300"
            >
              Hearth
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                className="text-xs text-zinc-500 transition-colors duration-200 hover:text-zinc-300"
              >
                Sign in
              </Link>
              <Link
                to="/register"
                className="text-xs text-zinc-500 transition-colors duration-200 hover:text-zinc-300"
              >
                Register
              </Link>
            </>
          )}
        </div>
      </footer>
      <style>{`
        .cpill {
          padding: 5px 14px;
          border: 1px solid rgb(39 39 42);
          color: rgb(113 113 122);
          font-size: 0.7rem;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          cursor: pointer;
          background: transparent;
          transition: all 0.15s;
        }
        .cpill:hover {
          color: white;
        }
        .cpill-active {
          background: rgb(251 191 36);
          color: rgb(9 9 11);
          border-color: rgb(251 191 36);
        }
        .lex-tag {
          font-size: 0.6rem;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          padding: 2px 6px;
          border: 1px solid rgb(39 39 42);
          color: rgb(82 82 91);
        }
      `}</style>
    </div>
  );
};
export default Lexicon;
